import { Button, Card, Loader, Modal, Tag } from "@caprikorn/dynamo-ui";
import { type FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useTenant } from "@contexts/Tenant/Tenant.context";
import { LoadingRound, Redo } from "@icons";
import { ROUTES } from "@router/router.constants";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useGetPerimeterExport } from "@services/apiEcms/useGetPerimeterExport";
import { usePostPerimeterExport } from "@services/apiEcms/usePostPerimeterExport";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./OverviewGroupCard.module.css";

export const OverviewGroupCard: FC = () => {
  const { t } = useCustomTranslation("main");
  const { supportEmail: defaultSupportEmail = "" } = useTenant();
  const navigate = useNavigate();
  const { currentGroup } = useGroupReferences();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: groupData, isLoading: isGroupDetailsLoading } = useGetGroup({
    groupId: currentGroup,
  });
  const supportEmail = groupData?.group.email || defaultSupportEmail;

  const {
    data: extractStatusData,
    isStale,
    refetch: extractPerimRefetch,
    isLoading: isExtractPerimeterLoading,
  } = useGetPerimeterExport({ isAdmin: Boolean(groupData?.isAdmin) });

  useEffect(() => {
    if (isStale && extractStatusData?.status === "IN_PROGRESS") {
      extractPerimRefetch({ cancelRefetch: false });
    }
  }, [extractPerimRefetch, extractStatusData, isStale]);

  const { mutate } = usePostPerimeterExport(supportEmail);

  if (isExtractPerimeterLoading || isGroupDetailsLoading) return <Loader />;

  if (!groupData?.group.name) {
    return null;
  }

  return (
    <Card data-testid="perimeter-extract-zone-card-header">
      <div className={classes.title}>
        <h3 className="font-title-lg">{groupData.group.name}</h3>

        {groupData?.isAdmin && extractStatusData?.status === "IN_PROGRESS" && (
          <Tag
            icon={LoadingRound}
            iconStyle="start"
            label={t("perimeter.header.extractInProgress")}
          />
        )}

        {groupData?.isAdmin && extractStatusData?.status !== "IN_PROGRESS" && (
          <Button
            onClick={() => {
              mutate(undefined, {
                onSuccess: () => {
                  setIsModalOpen(true);
                },
              });
            }}
            startIcon={Redo}
            variant="secondary"
          >
            {t("perimeter.header.extractButton")}
          </Button>
        )}
      </div>

      {isModalOpen && (
        <Modal maxSize="md" onClose={() => setIsModalOpen(false)}>
          <Modal.Header
            illustration="fastSavInstallation"
            title={t("perimeter.extractModal.beingGenerated")}
          />
          <Modal.Body className={classes.body}>
            {extractStatusData?.status !== "DONE" && (
              <p className="font-label-default">
                {t("perimeter.extractModal.generationHint")}
              </p>
            )}
            <p>{t("perimeter.extractModal.postGenerationInfo")}</p>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.ConfirmButton
              onClick={() => {
                navigate(ROUTES.DOCUMENTS);
              }}
            >
              {t("perimeter.extractModal.redirectButton")}
            </Modal.Footer.ConfirmButton>
          </Modal.Footer>
        </Modal>
      )}
    </Card>
  );
};
